// import { BillReviewTemplate } from "@shared/bill-cycle/BillReviewTemplate";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Switch } from "@shared/ui/switch";
import { Checkbox } from "@shared/ui/checkbox";
import { Label } from "@shared/ui/label";
import { Button } from "@shared/ui/button";
import {
  Mail,
  Truck,
  CheckCircle2,
  Save,
  Info,
  AlertCircle,
} from "lucide-react";
import { StepHelpers } from "@shared/components/Stepper";
import { useApproveCorrectedBill, useBillPreview } from "../hook";
import BillPdfPreview from "./components/BillPreview";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

interface ApprovalStepProps {
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
}

const VERIFICATION_ITEMS = [
  {
    id: "consumer_tariff_verified",
    label: "Consumer & Tariff Data Verified",
    description:
      "I confirm that consumer data, assigned plans, and tariff rates have been checked and verified against the records.",
  },
  {
    id: "meter_reading_verified",
    label: "Meter & Reading Data Verified",
    description:
      "I confirm that meter readings, consumption data, and meter status have been checked and verified for accuracy.",
  },
  {
    id: "payments_charges_verified",
    label: "Payments & Charges Verified",
    description:
      "I confirm that payments, service charges, and adjustments have been checked and verified.",
  },
  {
    id: "bill_calculations_verified",
    label: "Bill Calculations Verified",
    description:
      "I confirm that the final bill calculations, including totals and taxes, have been checked and verified.",
  },
];

export function ApprovalStep({
  stepHelpers,
  currentStepIndex = 1,
  onNext,
  onPrevious,
  setValidationError,
  clearValidationError,
}: ApprovalStepProps) {
  const navigate = useNavigate();
  const correctedId = stepHelpers.getStepData(0);
  const bill = stepHelpers.getStepData(3);
  const { data: billBlob } = useBillPreview(bill?.newBillId);
  const { mutate: approveBillCorrection } = useApproveCorrectedBill(
    correctedId?.correctionId,
  );
  const [sendEBill, setSendEBill] = useState(true);
  const [sendPhysicalBill, setSendPhysicalBill] = useState(false);
  const [consents, setConsents] = useState({
    consumer_tariff_verified: false,
    meter_reading_verified: false,
    payments_charges_verified: false,
    bill_calculations_verified: false,
  });

  const isApproveDisabled = !Object.values(consents).every(Boolean);

  const handleSaveAsDraft = () => {
    if (isApproveDisabled) {
      return;
    }
    const payload = {
      is_draft: true,
      delivery_preferences: {
        send_e_bill: sendEBill,
        send_physical_bill: sendPhysicalBill,
        consent_flags: consents,
      },
      comments: "All verified and approved",
    };

    approveBillCorrection(payload);
    onNext?.();
  };
  const handleApproveAndComplete = () => {
    if (isApproveDisabled) {
      return;
    }
    const payload = {
      delivery_preferences: {
        send_e_bill: sendEBill,
        send_physical_bill: sendPhysicalBill,
        consent_flags: consents,
      },
      comments: "All verified and approved",
    };

    approveBillCorrection(payload, {
      onSuccess: () => navigate("/bx/bill-generation/bill-correction"),
    });
  };

  const handleConsentChange = (id: string, value: boolean) => {
    setConsents((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Info Banners */}
      <div className="space-y-3">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-3">
          <div className="bg-blue-100 p-1.5 rounded-md mt-0.5">
            <Info className="h-4 w-4 text-blue-700" />
          </div>
          <div className="space-y-0.5">
            <p className="text-sm font-semibold text-blue-900">
              Bill Regeneration Process
            </p>
            <p className="text-xs text-blue-700">
              Upon approval, the original bill will be marked as{" "}
              <span className="font-semibold">Corrected</span> and a new bill
              entity will be generated reflecting these changes.
            </p>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-3">
          <div className="bg-blue-100 p-1.5 rounded-md mt-0.5">
            <Info className="h-4 w-4 text-blue-700" />
          </div>
          <div className="space-y-0.5">
            <p className="text-sm font-semibold text-blue-900">
              Review all bill details, calculations, and consumer information
              before generating the final bill.
            </p>
            <p className="text-xs text-blue-700">
              Review the generated bill details and approve to complete the
              process.
            </p>
          </div>
        </div>
      </div>
      <BillPdfPreview blob={billBlob} />

      {/* Bill Preview */}
      <div className="border rounded-xl overflow-hidden shadow-sm bg-white">
        {/* <BillReviewTemplate /> */}
      </div>

      {/* Delivery Options */}
      <div className="space-y-4">
        {bill?.selectedBill?.isEBill && (
          <Card className="bg-blue-50/30 border-blue-100 shadow-sm">
            <CardContent className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 flex-shrink-0">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm">
                    E-Bill Delivery
                  </h4>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Consumer has opted for electronic bill delivery. Bill will
                    be sent to{" "}
                    <span className="text-blue-600 font-medium">
                      {bill?.selectedBill?.consumerEmail}
                    </span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 pl-14 md:pl-0">
                <span className="text-xs font-medium text-gray-700">
                  Send E-Bill
                </span>
                <Switch checked={sendEBill} onCheckedChange={setSendEBill} />
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="bg-gray-50/50 border-gray-200 shadow-sm">
          <CardContent className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 flex-shrink-0">
                <Truck className="h-5 w-5" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 text-sm">
                  Physical Bill Delivery
                </h4>
                <p className="text-xs text-gray-500 mt-0.5">
                  Deliver a physical bill to:{" "}
                  <span className="font-medium text-gray-900">
                    {bill?.selectedBill?.consumerAddress}
                  </span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 pl-14 md:pl-0">
              <span className="text-xs font-medium text-gray-700">
                Deliver Physical Bill
              </span>
              <Switch
                checked={sendPhysicalBill}
                onCheckedChange={setSendPhysicalBill}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Verification & Consent */}
      <Card className="border-gray-200 shadow-sm">
        <CardHeader className="pb-3 border-b border-gray-100 bg-gray-50/50">
          <CardTitle className="text-sm font-bold flex items-center gap-2 text-gray-900">
            <CheckCircle2 className="h-4 w-4 text-blue-600" />
            Verification & Consent
          </CardTitle>
          <p className="text-xs text-gray-500">
            Please verify the following information before approving the bill.
          </p>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
            {VERIFICATION_ITEMS.map((item) => (
              <div key={item.id} className="flex items-start gap-3">
                <Checkbox
                  id={item.id}
                  checked={consents[item.id as keyof typeof consents]}
                  onCheckedChange={(v) =>
                    handleConsentChange(item.id, Boolean(v))
                  }
                />
                <div className="space-y-1">
                  <Label
                    htmlFor={item.id}
                    className="text-sm font-semibold text-gray-900"
                  >
                    {item.label}
                  </Label>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Footer Actions */}
      <div className="flex justify-between pt-4 border-t border-gray-200 mt-8">
        <Button
          variant="outline"
          onClick={onPrevious}
          className="px-6 bg-white hover:bg-gray-50 text-gray-700"
        >
          Previous
        </Button>
        <div className="flex gap-3">
          <Button
            onClick={handleSaveAsDraft}
            className="bg-blue-600 hover:bg-blue-700 text-white border-blue-600 px-6"
          >
            <Save className="h-4 w-4 mr-2" />
            Save As Draft
          </Button>
          <Button
            disabled={isApproveDisabled}
            onClick={handleApproveAndComplete}
            className="bg-green-600 hover:bg-green-700 text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Approve & Complete
          </Button>
        </div>
      </div>
    </div>
  );
}
